import { useRouter } from 'next/router';
import { ReactElement, useCallback, useEffect } from 'react';
import { getEnv } from '../../../../../../common/env/env';
import { t } from '../../../../../../common/getTranslations';
import { redirectInInitialProps } from '../../../../../../common/redirectInInitialProps';
import { requestContext } from '../../../../../../common/requestContext';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import { Layout } from '../../../../../../design-system/layout/Layout';
import { PcHeaderType } from '../../../../../../design-system/layout/PcHeaderType.enum';
import { CommentTabContainer } from '../../../../../../domain/content/[seriesId]/container/CommentTabContainer';
import { CommentTabControlAndLogContainer } from '../../../../../../domain/content/[seriesId]/container/CommentTabControlAndLogContainer';
import { ViewerEntryCheckerHeadless } from '../../../../../../domain/content/[seriesId]/viewer/[productId]/headless/ViewerEntryCheckerHeadless';
import { ViewerInfoFetcherHeadless } from '../../../../../../domain/content/[seriesId]/viewer/[productId]/headless/ViewerInfoFetcherHeadless';
import { setNonstopWatching, setViewerContinued } from '../../../../../../domain/content/[seriesId]/viewer/[productId]/state/viewerInfoSlice';
import { ApiErrorBoundary } from '../../../../../../errors/ApiErrorBoundary.class';
import { PagewebErrorType } from '../../../../../../errors/PagewebErrorType.enum';
import { contentFactory } from '../../../../../../factory/contentFactory';
import { storeFactory } from '../../../../../../factory/storeFactory';
import { ExceptionMobileHeaderType } from '../../../../../../header/constants';
import logger from '../../../../../../log/logger';
import { MetaInfo } from '../../../../../../meta/MetaInfo.interface';
import { CACHE_ONLY_CONFIG } from '../../../../../../reactQuery/constant';
import { createQueryClient } from '../../../../../../reactQuery/createQueryClient';
import { nextReduxWrapper } from '../../../../../../state/store';
interface ViewerCommentProps {
  seriesId: number;
  productId: number;
}
function ViewerComment({
  seriesId,
  productId
}: ViewerCommentProps) {
  const router = useRouter();
  const dispatch = useTypedDispatch();

  // 뷰어 이탈 시 정주행 정보 초기화
  const handleRouterChangeComplete = useCallback((url: string) => {
    // 시리즈 아이디가 같은 뷰어간의 이동에는 정주행 상태를 변경하지 않음
    if (url.includes('content') && url.includes('viewer') && seriesId.toString() === url.split('/')[2]) {
      return;
    }
    // 뷰어 이탈 시에 정주행 상태 초기화
    dispatch(setNonstopWatching({
      seriesId,
      isNonstopWatching: false
    }));
    dispatch(setViewerContinued({
      viewerContinued: 0
    }));
  }, [dispatch, seriesId]);
  useEffect(() => {
    router.events.on('routeChangeStart', handleRouterChangeComplete);
    return () => {
      router.events.off('routeChangeStart', handleRouterChangeComplete);
    };
  }, [handleRouterChangeComplete, router.events]);
  if (!requestContext.requestInfo || !seriesId || !productId) {
    return null;
  }
  return <ApiErrorBoundary>
      <ViewerEntryCheckerHeadless seriesId={seriesId} productId={productId}>
        <ViewerInfoFetcherHeadless seriesId={seriesId} productId={productId}>
          <CommentTabControlAndLogContainer seriesId={seriesId} productId={productId} initialSortType={router.query.sortType ? String(router.query.sortType) : null}>
            {({
            seriesId,
            productId,
            currentTab,
            initialSortType,
            onChangeTab
          }) => <CommentTabContainer seriesId={seriesId} productId={productId} currentTab={currentTab} initialSortType={initialSortType} onChangeTab={onChangeTab} />}
          </CommentTabControlAndLogContainer>
        </ViewerInfoFetcherHeadless>
      </ViewerEntryCheckerHeadless>
    </ApiErrorBoundary>;
}
ViewerComment.getInitialProps = nextReduxWrapper.getInitialPageProps(() => async context => {
  const seriesId = Number(context.query?.seriesId || '');
  const productId = Number(context.query?.productId || '');
  let metaInfo: MetaInfo | undefined = undefined;
  const queryClient = createQueryClient();
  try {
    const singlePreviewResult = await queryClient.fetchQuery(storeFactory.singlePreview({
      productId
    }, CACHE_ONLY_CONFIG, {
      ssrRequest: context.req
    }));
    const singleSimpleResult = await queryClient.fetchQuery(contentFactory.simpleSeriesSingleInfo({
      seriesId,
      productId
    }, CACHE_ONLY_CONFIG, {
      ssrRequest: context.req
    }));
    if (!singlePreviewResult.singlePreview) {
      throw new Error('singlePreview fetch 실패');
    }
    if (!singleSimpleResult.simpleSeriesSingleInfo) {
      throw new Error('singleSimple fetch 실패');
    }
    const {
      title,
      author,
      thumbnail,
      landThumbnail,
      seriesPreview
    } = singlePreviewResult.singlePreview;
    metaInfo = {
      title,
      author,
      keywords: `${title}, ${author}`,
      description: seriesPreview?.description ? seriesPreview.description.replace(/\n/g, '') : '',
      url: `${getEnv().webOrigin}/content/${seriesId}/viewer/${productId}`,
      image: landThumbnail || thumbnail
    };
  } catch (e) {
    //  잘못된 input 등일 때 apolloError 가 발생. 이 경우 404 로 띄워준다
    if ('isSerializedPagewebError' in e) {
      if (e.type === PagewebErrorType.ApolloError) {
        redirectInInitialProps({
          destination: '/404',
          res: context.res
        });
        return {};
      }
    }
    logger.err('뷰어 Generation 실패', e);
  }
  return {
    seriesId,
    productId,
    metaInfo
  };
});
ViewerComment.getLayout = function (page: ReactElement) {
  return <Layout title={t('comment_accessibility_content')} mobileLayoutConfig={{
    shouldShowHeader: true,
    headerType: ExceptionMobileHeaderType.Comment
  }} pcLayoutConfig={{
    shouldShowHeader: true,
    headerType: PcHeaderType.ViewerComment
  }}>
      {page}
    </Layout>;
};
export default ViewerComment;